import {
    useMutation,
    useQuery,
    useQueryClient,
    UseQueryOptions,
} from "react-query";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";
import { AxiosError } from "axios";

import { API } from "../../../utils/AxiosInstance";
import { apiDate } from "../../../utils/DateUtils";
import { Food } from "../../../models/Food";
import { IngredientApiResponse } from "../ai/types";

import { queryKey as generalFoodQueryKeys } from "../../foods/types";

type BackendErrorResponse = { errors?: Record<string, string[] | string> };

export const queryKeys = {
    customFoodDetail: (id: string) => ["customFoodDetail", id],
    favoriteCustomFoods: () => ["favoriteCustomFoods"],
    customFoods: () => ["customFoods"],
    customRecipeEnabled: () => ["customRecipeEnabled"],
};
export function useCustomFoodDetail(
    id: string,
    options: UseQueryOptions<Food, AxiosError> = {}
) {
    return useQuery<Food, AxiosError>(
        queryKeys.customFoodDetail(id),
        async () => {
            const { data } = await API.get<Food>(`/custom/${id}`);
            return data;
        },
        {
            enabled: !!id,
            refetchOnWindowFocus: false,
            ...options,
        }
    );
}
interface FavoriteResponse {
    favorite: boolean;
}

export function useToggleFavoriteCustomFood() {
    const qc = useQueryClient();
    const { t } = useTranslation();
    return useMutation<
        FavoriteResponse,
        AxiosError<BackendErrorResponse>,
        { id: string; isFavorite: boolean; calories?: number }
    >(
        ["toggleFavoriteCustomFood"],
        async ({ id, isFavorite, calories }) => {
            const { data } = await API.post<FavoriteResponse>(
                `/custom_recipes/${id}/favorite`,
                { is_favorite: isFavorite, calories: calories || 0 }
            );
            return data;
        },
        {
            onSuccess: (data, { id }) => {
                void qc.invalidateQueries(queryKeys.favoriteCustomFoods());
                void qc.invalidateQueries(queryKeys.customFoodDetail(id));
                void qc.invalidateQueries(generalFoodQueryKeys.favoriteFoods());

                toast.success(
                    data.favorite
                        ? t("recipe_book.favourite_toast")
                        : t("recipe_book.unfavourite_toast")
                );
            },
            onError: (err) => {
                const backendErrors = err.response?.data?.errors;
                toast.error(
                    backendErrors
                        ? Object.values(backendErrors).flat().join(", ")
                        : err.message || "Failed to update favorite status."
                );
            },
        }
    );
}
interface LogCustomFoodInput {
    id: string;
    mealType: string;
    date: Date;
    quantity: number;
    serving_unit: string;
    calories: number;
}
interface LogCustomFoodResponse {
    logged: boolean;
}

export function useLogCustomFood() {
    const { t } = useTranslation();
    const qc = useQueryClient();
    return useMutation<LogCustomFoodResponse, AxiosError, LogCustomFoodInput>(
        "logCustomFood",
        async ({ id, mealType, date, quantity, serving_unit, calories }) => {
            const { data } = await API.post<LogCustomFoodResponse>(
                `/custom_recipes/${id}/log`,
                {
                    meal_type: mealType.toUpperCase(),
                    date: apiDate(date),
                    quantity,
                    serving_unit,
                    calories,
                }
            );
            return data;
        },
        {
            onSuccess: () => {
                void qc.invalidateQueries(["foods", "meals"]);
                toast.success(t("recipe_book.recipe_log_toast"));
            },
            onError: () => {
                toast.error("Failed to log food. Please try again.");
            },
        }
    );
}
export function useCustomRecipeEnabled(cohortName?: string) {
    return useQuery(queryKeys.customRecipeEnabled(), async () => {
        const { data } = await API.get<boolean>(`/custom_recipes/enabled`, {
            params: { cohort_name: cohortName },
        });
        return data;
    });
}

export function useRecipeIngredientSearch(search: string) {
    return useQuery<IngredientApiResponse[], AxiosError>(
        ["recipeIngSearch", search],
        async () => {
            const { data } = await API.get<IngredientApiResponse[]>(
                "/custom_recipes/search_ingredients",
                { params: { query: search } }
            );
            return data;
        },
        {
            enabled: search.length > 1,
            staleTime: 5 * 60 * 1000,
            refetchOnWindowFocus: false,
        }
    );
}
export function useDeleteCustomFood() {
    const { t } = useTranslation();
    const qc = useQueryClient();
    return useMutation<void, AxiosError, string>(
        "deleteCustomFood",
        async (foodId) => {
            await API.delete(`/custom_recipes/${foodId}`);
        },
        {
            onSuccess: (_, foodId) => {
                void qc.invalidateQueries(queryKeys.customFoods());
                void qc.invalidateQueries(queryKeys.customFoodDetail(foodId));
                void qc.invalidateQueries(queryKeys.favoriteCustomFoods());
                toast.success(t("recipe_book.delete_toast"));
                void qc.invalidateQueries(["customFoods"]);
            },
            onError: () => {
                toast.error("Failed to delete recipe.");
            },
        }
    );
}
